import {
  useIsMobile,
  WSButton,
  WSContextMenu,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSPage,
  WSPreview,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import image1 from "../../../assets/images/payables-feature-1.png";
import image2 from "../../../assets/images/payables-feature-2.png";
import image3 from "../../../assets/images/payables-feature-3.png";
import image4 from "../../../assets/images/payables-feature-4.png";
import { WSPersistentUpgradeButton } from "../../../components/Membership/WSPersistentUpgradeButton";
import { Tabs } from "../../../components/Tabs/Tabs";
import { useUserId } from "../../../query/hooks/helpers";
import { usePayrollSettings } from "../../../query/payments/queries";
import { useActivities } from "../../../query/users/queries";
import {
  RenderDefaultPageErrorMessage,
  WSQueries
} from "../../../query/WSQuery";
import { WSFrontendFeature } from "../../../Settings/utils/subscriptionUtils";
import { PaymentsStatusBanner } from "../../components/PaymentsStatusBanner/PaymentsStatusBanner";
import { PaymentsSetup } from "../paymentsSetup";
import { CreatePayable } from "./CreatePayable";
import { EditPayable } from "./EditPayable";
import styles from "./Payables.module.scss";
import { PayablesAll } from "./PayablesAll";
import { PayablesDashboard } from "./PayablesDashboard";
import { PayablesDisputed } from "./PayablesDisputed";
import { PayablesDrafts } from "./PayablesDrafts";
import { PayablesDualStageToApprove } from "./PayablesDualStageToApprove";
import { PayablesDualStageToPreApprove } from "./PayablesDualStageToPreApprove";
import { PayablesPaid } from "./PayablesPaid";
import { PayablesPayrollRuns } from "./PayablesPayrollRuns";
import { PayablesScheduled } from "./PayablesScheduled";
import { PayablesToApprove } from "./PayablesToApprove";
import { PayrollRun } from "./PayrollRun";
import { PayrollSettingsModal } from "./PayrollSettingsModal";

export const Payables: React.FC = () => {
  const userId = useUserId();
  const history = useHistory();
  const location = useLocation();
  const payrollSettingsQuery = usePayrollSettings(userId, { retry: false });
  const isMobile = useIsMobile();
  const activityQuery = useActivities(userId);

  return (
    <WSQueries
      queries={{
        activityQuery
      }}
    >
      {({ activityQuery: { data: activity } }) => {
        let tabs = [
          {
            title: "Dashboard",
            path: "/member/invoices/payables/dashboard"
          },
          {
            title: "To Approve",
            path: "/member/invoices/payables/to-approve"
          },
          {
            title: "Scheduled",
            path: "/member/invoices/payables/scheduled"
          },
          {
            title: "Paid",
            path: "/member/invoices/payables/paid"
          },
          {
            title: "Payroll Runs",
            path: "/member/invoices/payables/payroll-runs"
          },
          {
            title: "All Payables",
            path: "/member/invoices/payables/all"
          }
        ];
        if (
          payrollSettingsQuery.data?.workflow ===
          PayrollWorkflowStrategy.DualStage
        ) {
          tabs = [
            {
              title: "Dashboard",
              path: "/member/invoices/payables/dashboard"
            },
            {
              title: "To Pre-Approve",
              path: "/member/invoices/payables/to-preapprove"
            },
            {
              title: "To Approve",
              path: "/member/invoices/payables/to-approve"
            },
            {
              title: "Scheduled",
              path: "/member/invoices/payables/scheduled"
            },
            {
              title: "Paid",
              path: "/member/invoices/payables/paid"
            },
            {
              title: "Payroll Runs",
              path: "/member/invoices/payables/payroll-runs"
            },
            {
              title: "All Payables",
              path: "/member/invoices/payables/all"
            }
          ];
        }
        return (
          <Switch>
            <Route
              path="/member/invoices/payables/create"
              component={CreatePayable}
            />

            <Route
              path="/member/invoices/payables/:payableId/edit"
              component={EditPayable}
            />

            <Route
              path="/member/invoices/payables/draft"
              component={PayablesDrafts}
            />

            <Route
              path="/member/invoices/payables/disputed"
              component={PayablesDisputed}
            />

            <Route
              path="/member/invoices/payables/payroll-runs/:payrollRunId"
              component={PayrollRun}
            />

            <Route
              path="/member/invoices/payables/set-up"
              render={() => (
                <PaymentsSetup
                  basePath="/member/invoices/payables/set-up"
                  onBack={() => {
                    history.push("/member/invoices/payables");
                  }}
                  onFinish={() => {
                    history.push("/member/invoices/payables");
                  }}
                />
              )}
            />

            <Route
              path="/member/invoices/payables"
              render={() =>
                activity.flows.paymentsSetup?.complete ? (
                  <WSPage
                    title="Payables"
                    primaryButtonComponent={
                      isMobile
                        ? props => (
                            <WSPersistentUpgradeButton
                              name="createPayable"
                              feature={
                                WSFrontendFeature.InviteAndPayCollaborator
                              }
                              onClick={() => {
                                history.push({
                                  pathname: "/member/invoices/payables/create",
                                  state: {
                                    backPath: location.pathname
                                  }
                                });
                              }}
                              {...props}
                            >
                              Create payable
                            </WSPersistentUpgradeButton>
                          )
                        : undefined
                    }
                    sideContent={
                      isMobile ? (
                        undefined
                      ) : (
                        <WSContextMenu
                          isSameWidthAsTrigger
                          className={styles.buttonActionsWrapper}
                          name="actions"
                          title="Add Payable"
                          renderMenuTriggerView={
                            <WSFlexBox
                              ml="S"
                              mb="XS"
                              className={styles.buttonActions}
                              alignItems="stretch"
                            >
                              <WSText.Heading4
                                align="center"
                                color="white"
                                className={styles.buttonActionsText}
                                py="S"
                                px="M"
                                onClick={() => {
                                  history.push({
                                    pathname:
                                      "/member/invoices/payables/create",
                                    state: {
                                      backPath: location.pathname
                                    }
                                  });
                                }}
                                data-testid="createPayableButton"
                              >
                                Create payable
                              </WSText.Heading4>
                              <WSFlexBox.Center px="S">
                                <WSIcon
                                  name="dropDown"
                                  color="white"
                                  size="S"
                                  className={styles.buttonActionsIcon}
                                />
                              </WSFlexBox.Center>
                            </WSFlexBox>
                          }
                          defaultPlacement="bottom-start"
                          menuItems={[
                            {
                              label: "Bulk upload payables",
                              onClick() {
                                history.push(
                                  "/member/invoices/payables/bulk-upload/info"
                                );
                              }
                            }
                          ]}
                        />
                      )
                    }
                  >
                    <PaymentsStatusBanner mb="L" />
                    <PayrollSettingsModal />
                    <Tabs links={tabs} />

                    <WSElement mt="L" data-testid="payablesContainer">
                      <Switch>
                        <Route
                          path="/member/invoices/payables/dashboard"
                          component={PayablesDashboard}
                        />

                        <Route
                          path="/member/invoices/payables/to-preapprove"
                          component={PayablesDualStageToPreApprove}
                        />

                        <Route
                          path="/member/invoices/payables/to-approve"
                          component={
                            payrollSettingsQuery.data?.workflow ===
                            PayrollWorkflowStrategy.DualStage
                              ? PayablesDualStageToApprove
                              : PayablesToApprove
                          }
                        />

                        <Route
                          path="/member/invoices/payables/scheduled"
                          component={PayablesScheduled}
                        />

                        <Route
                          path="/member/invoices/payables/paid"
                          component={PayablesPaid}
                        />

                        <Route
                          path="/member/invoices/payables/payroll-runs"
                          component={PayablesPayrollRuns}
                        />

                        <Route
                          path="/member/invoices/payables/all"
                          component={PayablesAll}
                        />

                        <Redirect
                          from="/member/invoices/payables"
                          to="/member/invoices/payables/dashboard"
                        />
                      </Switch>
                    </WSElement>
                  </WSPage>
                ) : (
                  <WSPage>
                    <WSGrid.Container>
                      <WSGrid.Item L={5}>
                        <WSText.Heading2 mb="S">Payables</WSText.Heading2>
                        <WSText mb={isMobile ? "M" : "XL"} color="grey700">
                          Fast, affordable payments for your subcontractors and
                          payroll for your freelance workforce.
                        </WSText>

                        <WSButton
                          onClick={() => {
                            history.push("/member/invoices/payables/set-up");
                          }}
                          name="setUpPayments"
                        >
                          Set up Payments
                        </WSButton>
                      </WSGrid.Item>
                    </WSGrid.Container>

                    <WSPreview
                      mt={isMobile ? "L" : "XXL"}
                      list={[
                        {
                          title: "Faster payments",
                          description:
                            "Whether you’ve been billed or are creating payables yourself, taking care of payments is no longer a chore.",
                          image: image1,
                          imageWidth: 284
                        },
                        {
                          title: "Scalable payroll",
                          description:
                            "Easily approve and automate payroll cycles. Integrate with your existing accounting software.",
                          image: image2,
                          imageWidth: 395
                        },
                        {
                          title: "Payment notifications",
                          description:
                            "Transparent tracking so you and your team know exactly when funds land in their account.",
                          image: image3,
                          imageWidth: 335
                        },
                        {
                          title: "Taxes and compliance",
                          description:
                            "Wingspan helps with generating 1099s and other tax documents, so you'll be covered come tax time.",
                          image: image4,
                          imageWidth: 396
                        }
                      ]}
                    />
                  </WSPage>
                )
              }
            />
          </Switch>
        );
      }}
    </WSQueries>
  );
};
